/**
 * Admin API functions
 */
import { ENDPOINTS } from './endpoints';
import { getRequestBackendDataV2, postRequestBackendDataV2 } from './core';

export async function getAdminUsersV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USERS);
}

export async function getAdminUsersUsageSummaryV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USERS_USAGE_SUMMARY);
}

export async function getAdminUserConfigV2(auth, callback, userId) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USER_CONFIG(userId));
}

export async function updateAdminUserConfigV2(auth, callback, userId, config) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USER_CONFIG(userId), config, 'PUT');
}

export async function updateAdminUserStatusV2(auth, callback, userId, status) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USER_STATUS(userId), status, 'PUT');
}

export async function resetUserDailyUsageV2(auth, callback, userId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USER_RESET_USAGE(userId), {}, 'POST');
}

export async function getAdminErrorsV2(auth, callback, userId = null, limit = null) {
    let path = ENDPOINTS.ADMIN.ERRORS;
    if (userId || limit) {
        path += '?';
        if (userId) path += `userId=${userId}&`;
        if (limit) path += `limit=${limit}`;
    }
    await getRequestBackendDataV2(auth, callback, path);
}

export async function getAdminErrorByIdV2(auth, callback, errorId) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.ERROR_BY_ID(errorId));
}

export async function deleteAdminErrorV2(auth, callback, errorId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.ERROR_BY_ID(errorId), {}, 'DELETE');
}

export async function setKeyValueV2(auth, callback, data) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.KEY_VALUE, data);
}

export async function getAllKeyValuesV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.KEY_VALUES);
}

export async function getServicesStatusV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICES_STATUS);
}

export async function testAnalysisServiceV2(auth, callback) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICE_TEST, {}, 'POST');
}

export async function executeAdminToolV2(auth, callback, toolName, args) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.TOOL_TEST, { toolName, args }, 'POST');
}

// Subscription and tier management
export async function getSubscriptionTiersV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_TIERS);
}

export async function updateSubscriptionTierV2(auth, callback, tierId, tierData) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_TIER(tierId), tierData, 'PUT');
}

export async function createSubscriptionTierV2(auth, callback, tierData) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_TIERS, tierData, 'POST');
}

export async function getSubscriptionUsersV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_USERS);
}

export async function setUserSubscriptionTierV2(auth, callback, userId, tierId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_USER_TIER(userId), { tierId }, 'PUT');
}

export async function getFeatureFlagsAdminV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_FEATURE_FLAGS);
}

export async function updateFeatureFlagsV2(auth, callback, flags) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_FEATURE_FLAGS, flags, 'PUT');
}

export async function updateSingleFeatureFlagV2(auth, callback, flagName, value) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SUBSCRIPTION_FEATURE_FLAG(flagName), { value }, 'PUT');
}

// Hierarchical feature flags
export async function getAllFeatureFlagsV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.FEATURE_FLAGS_ALL);
}

export async function setFeatureFlagV2(auth, callback, key, value, description = 'Feature flag') {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.FEATURE_FLAGS_SET, { key, value, description }, 'POST');
}

export async function deleteFeatureFlagV2(auth, callback, key) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.FEATURE_FLAGS_DELETE, { key }, 'DELETE');
}

// Economic Calendar
export async function getCalendarStatusV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.CALENDAR_STATUS);
}

export async function refreshCalendarV2(auth, callback) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.CALENDAR_REFRESH, {}, 'POST');
}

export async function getCalendarEventsV2(auth, callback, filters = {}) {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.impact) params.append('impact', filters.impact);
    if (filters.instrument) params.append('instrument', filters.instrument);
    if (filters.hoursBack) params.append('hoursBack', filters.hoursBack);
    if (filters.hoursForward) params.append('hoursForward', filters.hoursForward);

    const queryString = params.toString();
    const path = queryString ? `${ENDPOINTS.ADMIN.CALENDAR_EVENTS}?${queryString}` : ENDPOINTS.ADMIN.CALENDAR_EVENTS;
    await getRequestBackendDataV2(auth, callback, path);
}

export async function simulateEventProximityV2(auth, callback, config) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.CALENDAR_SIMULATE, config, 'POST');
}

export async function setCalendarProviderEnabledV2(auth, provider, enabled, callback) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.CALENDAR_PROVIDER_ENABLED(provider), { enabled }, 'POST');
}

export async function triggerIntegrityCheckV2(auth, callback) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.CALENDAR_INTEGRITY_CHECK, {}, 'POST');
}

export async function clearAllCalendarEventsV2(auth, callback) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.CALENDAR_EVENTS, {}, 'DELETE');
}

// User deletion
export async function deleteAdminUserV2(auth, callback, userId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.USER_DELETE(userId), {}, 'DELETE');
}

export async function getDeletionHistoryV2(auth, callback, email = null) {
    let path = ENDPOINTS.ADMIN.DELETION_HISTORY;
    if (email) {
        path += `?email=${encodeURIComponent(email)}`;
    }
    await getRequestBackendDataV2(auth, callback, path);
}

export async function retryDeletionV2(auth, callback, historyId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.DELETION_RETRY(historyId), {}, 'POST');
}

// OAuth Client Management
export async function getOAuthClientsV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.OAUTH_CLIENTS);
}

export async function createOAuthClientV2(auth, callback, clientData) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.OAUTH_CLIENTS, clientData, 'POST');
}

export async function updateOAuthClientV2(auth, callback, clientId, updateData) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.OAUTH_CLIENT(clientId), updateData, 'PUT');
}

export async function deleteOAuthClientV2(auth, callback, clientId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.OAUTH_CLIENT(clientId), {}, 'DELETE');
}

export async function regenerateOAuthClientSecretV2(auth, callback, clientId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.OAUTH_CLIENT_REGENERATE(clientId), {}, 'POST');
}

// Service API Keys Management
export async function getServiceApiKeysV2(auth, callback) {
    await getRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICE_KEYS);
}

export async function createServiceApiKeyV2(auth, callback, keyData) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICE_KEYS, keyData, 'POST');
}

export async function updateServiceKeyScopesV2(auth, callback, keyId, scopes) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICE_KEY_SCOPES(keyId), { scopes }, 'PUT');
}

export async function deactivateServiceApiKeyV2(auth, callback, keyId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICE_KEY(keyId), {}, 'DELETE');
}

export async function deleteServiceApiKeyPermanentV2(auth, callback, keyId) {
    await postRequestBackendDataV2(auth, callback, ENDPOINTS.ADMIN.SERVICE_KEY_DELETE_PERMANENT(keyId), {}, 'DELETE');
}
